import React, { memo, useCallback, useEffect, useId, useMemo, useRef, useState } from 'react';
import { IconMaximize as Maximize, IconMinus as Minus, IconPlus as Plus } from '@tabler/icons-react';
import { SEAT_RADIUS, seatTitle, type Point, type SeatInfo, type SeatingLayout } from './seating';
import { ShapeContent, shapeIconId } from '@/components/seatmap-studio/objectIcons';

export type SeatLook = {
    fill: string;
    stroke: string;
    text: string;
    strokeWidth?: number;
    check?: 'light' | 'dark';
    opacity?: number;
    disabled?: boolean;
};

export const HATCH = 'hatch';

type Props = {
    layout: SeatingLayout;
    look: (id: string, selected: boolean) => SeatLook;
    tooltip: (seat: SeatInfo) => React.ReactNode;
    selection: Set<string>;
    onSelectionChange: (next: Set<string>) => void;
    mode?: 'manage' | 'pick';
    readOnly?: boolean;
    onBlockedSeat?: (seat: SeatInfo) => void;
};

type View = { x: number; y: number; k: number };
type Drag =
    | { type: 'pan'; sx: number; sy: number; view: View; moved: boolean; tap?: { seatId: string | null; rowId: string | null } | null }
    | { type: 'pinch'; dist: number; mid: Point; view: View }
    | { type: 'box'; a: Point; b: Point; mode: 'replace' | 'add' | 'remove' };

const clampK = (k: number) => Math.min(6, Math.max(0.1, k));
const TAP_ZOOM_BELOW = 0.65;
const TAP_ZOOM_TO = 1.4;

type SeatsProps = {
    seats: SeatInfo[];
    look: (id: string, selected: boolean) => SeatLook;
    selection: Set<string>;
    showNumbers: boolean;
    hatchId: string;
};

const Seats = memo(({ seats, look, selection, showNumbers, hatchId }: SeatsProps) => (
    <g>
        {seats.map(s => {
            const selected = selection.has(s.id);
            const l = look(s.id, selected);
            const checkColor = l.check === 'dark' ? '#080808' : '#FFFFFF';
            return (
                <g key={s.id} opacity={l.opacity}>
                    <circle
                        data-seat={s.id}
                        cx={s.x}
                        cy={s.y}
                        r={SEAT_RADIUS}
                        fill={l.fill === HATCH ? `url(#${hatchId})` : l.fill}
                        stroke={l.stroke}
                        strokeWidth={l.strokeWidth ?? 1}
                        className={l.disabled ? 'cursor-not-allowed' : 'cursor-pointer'}
                    />
                    {l.check && (!showNumbers || selected) ? (
                        <path d={`M ${s.x - 4.5} ${s.y + 0.5} l 3 3 l 6 -6.5`} stroke={checkColor} strokeWidth={1.8} strokeLinecap="round" strokeLinejoin="round" fill="none" pointerEvents="none" />
                    ) : showNumbers && !l.disabled ? (
                        <text x={s.x} y={s.y} dy=".35em" textAnchor="middle" fontSize={8.5} fontWeight={600} fill={l.text} pointerEvents="none" className="select-none">
                            {s.number}
                        </text>
                    ) : null}
                    {s.accessible && <circle cx={s.x + 7.5} cy={s.y - 7.5} r={3.5} fill="#2563EB" stroke="#fff" strokeWidth={1} pointerEvents="none" />}
                </g>
            );
        })}
    </g>
));

type MinimapProps = {
    layout: SeatingLayout;
    look: (id: string, selected: boolean) => SeatLook;
    selection: Set<string>;
    view: View;
    size: { width: number; height: number };
    compact: boolean;
    onCenter: (world: Point) => void;
};

const MinimapSeats = memo(({ layout, look, selection, s, ox, oy }: { layout: SeatingLayout; look: MinimapProps['look']; selection: Set<string>; s: number; ox: number; oy: number }) => {
    const r = Math.max(0.9, SEAT_RADIUS * s);
    const b = layout.bounds;
    return (
        <g>
            {layout.shapes.map(sh => (
                <rect
                    key={sh.key}
                    x={ox + (sh.position.x - b.x) * s}
                    y={oy + (sh.position.y - b.y) * s}
                    width={Math.max(1, sh.size.width * s)}
                    height={Math.max(1, sh.size.height * s)}
                    rx={1}
                    fill={sh.variant === 'text' ? 'transparent' : sh.color || '#FDE68A'}
                    opacity={0.8}
                />
            ))}
            {layout.seats.map(seat => {
                const selected = selection.has(seat.id);
                const l = look(seat.id, selected);
                return (
                    <circle
                        key={seat.id}
                        cx={ox + (seat.x - b.x) * s}
                        cy={oy + (seat.y - b.y) * s}
                        r={selected ? r * 1.6 : r}
                        fill={l.fill === HATCH ? '#F59E0B' : l.fill.toUpperCase() === '#FFFFFF' ? '#CBD5E1' : l.fill}
                        opacity={l.opacity}
                    />
                );
            })}
        </g>
    );
});

const Minimap: React.FC<MinimapProps> = ({ layout, look, selection, view, size, compact, onCenter }) => {
    const b = layout.bounds;
    const W = compact ? 112 : 160;
    const H = Math.round(Math.min(compact ? 90 : 120, Math.max(56, (W * b.height) / Math.max(1, b.width))));
    const s = Math.min((W - 8) / b.width, (H - 8) / b.height);
    const ox = (W - b.width * s) / 2;
    const oy = (H - b.height * s) / 2;
    const dragging = useRef(false);

    const vx = (-view.x / view.k - b.x) * s + ox;
    const vy = (-view.y / view.k - b.y) * s + oy;
    const vw = (size.width / view.k) * s;
    const vh = (size.height / view.k) * s;

    const centerAt = (e: React.PointerEvent<SVGSVGElement>) => {
        const r = e.currentTarget.getBoundingClientRect();
        onCenter({ x: b.x + (e.clientX - r.left - ox) / s, y: b.y + (e.clientY - r.top - oy) / s });
    };

    return (
        <svg
            width={W}
            height={H}
            role="img"
            aria-label="Seat map overview. Tap or drag to move the view"
            className="absolute right-3 top-3 z-10 rounded-md border border-gray-200 bg-white/95 shadow-md touch-none cursor-pointer dark:border-nightBorder dark:bg-nightElevated/95"
            onPointerDown={e => {
                e.stopPropagation();
                dragging.current = true;
                e.currentTarget.setPointerCapture(e.pointerId);
                centerAt(e);
            }}
            onPointerMove={e => dragging.current && centerAt(e)}
            onPointerUp={e => {
                dragging.current = false;
                if (e.currentTarget.hasPointerCapture(e.pointerId)) e.currentTarget.releasePointerCapture(e.pointerId);
            }}
            onPointerCancel={() => { dragging.current = false; }}
        >
            <MinimapSeats layout={layout} look={look} selection={selection} s={s} ox={ox} oy={oy} />
            <rect x={vx} y={vy} width={vw} height={vh} fill="#0EA5E9" fillOpacity={0.12} stroke="#0EA5E9" strokeWidth={1.5} rx={2} pointerEvents="none" />
        </svg>
    );
};

const SeatingCanvas: React.FC<Props> = ({ layout, look, tooltip, selection, onSelectionChange, mode = 'manage', readOnly, onBlockedSeat }) => {
    const pickMode = mode === 'pick';
    const wrapRef = useRef<HTMLDivElement>(null);
    const svgRef = useRef<SVGSVGElement>(null);
    const hatchId = `hold-${useId().replace(/:/g, '')}`;
    const [size, setSize] = useState({ width: 0, height: 0 });
    const [view, setView] = useState<View>({ x: 0, y: 0, k: 1 });
    const viewRef = useRef(view);
    viewRef.current = view;
    const [drag, setDragState] = useState<Drag | null>(null);
    const dragRef = useRef<Drag | null>(null);
    const setDrag = (d: Drag | null) => {
        dragRef.current = d;
        setDragState(d);
    };
    const [hover, setHover] = useState<{ id: string; x: number; y: number } | null>(null);
    const fitted = useRef(false);

    const seatById = useMemo(() => new Map(layout.seats.map(s => [s.id, s])), [layout]);
    const rowById = useMemo(() => new Map(layout.rows.map(r => [r.id, r])), [layout]);

    const fit = useCallback(() => {
        const rect = wrapRef.current?.getBoundingClientRect();
        if (!rect || !rect.width) return;
        const b = layout.bounds;
        const k = clampK(Math.min(rect.width / b.width, rect.height / b.height) * 0.95);
        setView({ k, x: rect.width / 2 - (b.x + b.width / 2) * k, y: rect.height / 2 - (b.y + b.height / 2) * k });
    }, [layout]);

    const animRef = useRef<number | null>(null);
    const stopAnimation = () => {
        if (animRef.current !== null) cancelAnimationFrame(animRef.current);
        animRef.current = null;
    };
    useEffect(() => stopAnimation, []);

    const zoomToPoint = (world: Point, k: number) => {
        stopAnimation();
        const { width: W, height: H } = size;
        const from = viewRef.current;
        const c0 = { x: (W / 2 - from.x) / from.k, y: (H / 2 - from.y) / from.k };
        const start = performance.now();
        const step = (now: number) => {
            const t = Math.min(1, (now - start) / 320);
            const e = 1 - Math.pow(1 - t, 3);
            const kk = from.k * Math.pow(k / from.k, e);
            const cx = c0.x + (world.x - c0.x) * e;
            const cy = c0.y + (world.y - c0.y) * e;
            setView({ k: kk, x: W / 2 - cx * kk, y: H / 2 - cy * kk });
            animRef.current = t < 1 ? requestAnimationFrame(step) : null;
        };
        animRef.current = requestAnimationFrame(step);
    };

    const zoomAt = useCallback((factor: number, at?: Point) => {
        setView(v => {
            const k = clampK(v.k * factor);
            const c = at ?? { x: size.width / 2, y: size.height / 2 };
            return { k, x: c.x - ((c.x - v.x) / v.k) * k, y: c.y - ((c.y - v.y) / v.k) * k };
        });
    }, [size]);

    useEffect(() => {
        const el = wrapRef.current;
        if (!el) return;
        const ro = new ResizeObserver(([e]) => setSize({ width: e.contentRect.width, height: e.contentRect.height }));
        ro.observe(el);
        return () => ro.disconnect();
    }, []);

    useEffect(() => {
        if (!size.width || fitted.current) return;
        fitted.current = true;
        fit();
    }, [size.width, fit]);

    useEffect(() => {
        fitted.current = false;
    }, [layout]);

    useEffect(() => {
        const el = svgRef.current;
        if (!el) return;
        const onWheel = (e: WheelEvent) => {
            e.preventDefault();
            stopAnimation();
            const r = el.getBoundingClientRect();
            if (e.ctrlKey || e.metaKey) zoomAt(Math.exp(-e.deltaY * 0.01), { x: e.clientX - r.left, y: e.clientY - r.top });
            else setView(v => ({ ...v, x: v.x - e.deltaX, y: v.y - e.deltaY }));
        };
        el.addEventListener('wheel', onWheel, { passive: false });
        return () => el.removeEventListener('wheel', onWheel);
    }, [zoomAt]);

    const toWorld = (e: { clientX: number; clientY: number }): Point => {
        const r = svgRef.current!.getBoundingClientRect();
        const v = viewRef.current;
        return { x: (e.clientX - r.left - v.x) / v.k, y: (e.clientY - r.top - v.y) / v.k };
    };

    const toggle = (ids: string[], forceAdd?: boolean) => {
        const next = new Set(selection);
        const allIn = ids.every(id => next.has(id));
        ids.forEach(id => (allIn && !forceAdd ? next.delete(id) : next.add(id)));
        onSelectionChange(next);
    };

    const lastPress = useRef<{ x: number; y: number; t: number } | null>(null);
    const isDoublePress = (e: React.PointerEvent) => {
        const now = performance.now();
        const prev = lastPress.current;
        const dbl = e.detail >= 2 || (!!prev && now - prev.t < 350 && Math.hypot(e.clientX - prev.x, e.clientY - prev.y) < 6);
        lastPress.current = dbl ? null : { x: e.clientX, y: e.clientY, t: now };
        return dbl;
    };

    const pointers = useRef(new Map<number, Point>());

    const pickAt = (target: Element) => ({
        seatId: target.closest('[data-seat]')?.getAttribute('data-seat') ?? null,
        rowId: target.closest('[data-row]')?.getAttribute('data-row') ?? null,
    });

    const applyPick = (pick: { seatId: string | null; rowId: string | null }, alt: boolean, dbl: boolean) => {
        if (pick.seatId && pickMode) {
            const seat = seatById.get(pick.seatId);
            if (seat && viewRef.current.k < TAP_ZOOM_BELOW) {
                zoomToPoint(seat, TAP_ZOOM_TO);
                return true;
            }
            if (look(pick.seatId, selection.has(pick.seatId)).disabled && !selection.has(pick.seatId)) {
                if (seat) onBlockedSeat?.(seat);
            } else {
                toggle([pick.seatId]);
            }
            return true;
        }
        if (pick.rowId && pickMode) return false;
        if (pick.seatId) {
            if (alt || dbl) {
                const row = rowById.get(seatById.get(pick.seatId)?.rowId ?? '');
                if (row) toggle(row.seatIds, dbl);
            } else {
                toggle([pick.seatId]);
            }
            return true;
        }
        if (pick.rowId) {
            const row = rowById.get(pick.rowId);
            if (row) toggle(row.seatIds);
            return true;
        }
        return false;
    };

    const startPinch = () => {
        const [a, b] = [...pointers.current.values()];
        setDrag({ type: 'pinch', dist: Math.max(1, Math.hypot(a.x - b.x, a.y - b.y)), mid: { x: (a.x + b.x) / 2, y: (a.y + b.y) / 2 }, view: viewRef.current });
    };

    const onPointerDown = (e: React.PointerEvent<SVGSVGElement>) => {
        stopAnimation();
        const r = svgRef.current!.getBoundingClientRect();
        pointers.current.set(e.pointerId, { x: e.clientX - r.left, y: e.clientY - r.top });
        svgRef.current!.setPointerCapture(e.pointerId);
        if (pointers.current.size >= 2) {
            setHover(null);
            startPinch();
            return;
        }

        const touch = e.pointerType !== 'mouse';
        const pick = readOnly ? { seatId: null, rowId: null } : pickAt(e.target as Element);
        if (!touch && e.button === 0 && applyPick(pick, e.altKey, isDoublePress(e))) return;

        if (readOnly || touch || pickMode || e.button !== 0) {
            setDrag({ type: 'pan', sx: e.clientX, sy: e.clientY, view: viewRef.current, moved: false, tap: touch && e.button === 0 ? pick : null });
            return;
        }
        const p = toWorld(e);
        setDrag({ type: 'box', a: p, b: p, mode: e.altKey ? 'remove' : e.shiftKey || e.metaKey || e.ctrlKey ? 'add' : 'replace' });
    };

    const onPointerMove = (e: React.PointerEvent<SVGSVGElement>) => {
        const r = svgRef.current!.getBoundingClientRect();
        if (pointers.current.has(e.pointerId)) pointers.current.set(e.pointerId, { x: e.clientX - r.left, y: e.clientY - r.top });
        const d = dragRef.current;
        if (d?.type === 'pinch' && pointers.current.size >= 2) {
            const [a, b] = [...pointers.current.values()];
            const dist = Math.max(1, Math.hypot(a.x - b.x, a.y - b.y));
            const mid = { x: (a.x + b.x) / 2, y: (a.y + b.y) / 2 };
            const k = clampK(d.view.k * (dist / d.dist));
            const wx = (d.mid.x - d.view.x) / d.view.k;
            const wy = (d.mid.y - d.view.y) / d.view.k;
            setView({ k, x: mid.x - wx * k, y: mid.y - wy * k });
            return;
        }
        if (d?.type === 'pan') {
            const dx = e.clientX - d.sx;
            const dy = e.clientY - d.sy;
            const moved = d.moved || Math.hypot(dx, dy) > (d.tap ? 8 : 3);
            setDrag({ ...d, moved });
            if (moved) setView({ ...d.view, x: d.view.x + dx, y: d.view.y + dy });
            return;
        }
        if (d?.type === 'box') {
            setDrag({ ...d, b: toWorld(e) });
            return;
        }
        if (e.pointerType !== 'mouse') return;
        const seatId = (e.target as Element).closest('[data-seat]')?.getAttribute('data-seat');
        const wr = wrapRef.current!.getBoundingClientRect();
        setHover(seatId ? { id: seatId, x: e.clientX - wr.left, y: e.clientY - wr.top } : null);
    };

    const onPointerUp = (e: React.PointerEvent<SVGSVGElement>) => {
        pointers.current.delete(e.pointerId);
        if (svgRef.current?.hasPointerCapture(e.pointerId)) svgRef.current.releasePointerCapture(e.pointerId);
        const d = dragRef.current;
        if (d?.type === 'pinch') {
            if (pointers.current.size < 2) setDrag(null);
            return;
        }
        setDrag(null);
        if (d?.type === 'pan') {
            if (!d.moved && d.tap && !applyPick(d.tap, false, isDoublePress(e)) && selection.size && !pickMode) onSelectionChange(new Set());
            return;
        }
        if (d?.type !== 'box') return;
        const x0 = Math.min(d.a.x, d.b.x), x1 = Math.max(d.a.x, d.b.x);
        const y0 = Math.min(d.a.y, d.b.y), y1 = Math.max(d.a.y, d.b.y);
        const tiny = (x1 - x0) * viewRef.current.k < 4 && (y1 - y0) * viewRef.current.k < 4;
        if (tiny) {
            if (d.mode === 'replace' && selection.size) onSelectionChange(new Set());
            return;
        }
        const hits = layout.seats.filter(s => s.x >= x0 && s.x <= x1 && s.y >= y0 && s.y <= y1).map(s => s.id);
        const next = d.mode === 'replace' ? new Set<string>() : new Set(selection);
        hits.forEach(id => (d.mode === 'remove' ? next.delete(id) : next.add(id)));
        onSelectionChange(next);
    };

    const k = view.k;
    const coversAll = (() => {
        const b = layout.bounds;
        const x0 = -view.x / k, y0 = -view.y / k;
        return x0 <= b.x + 1 && y0 <= b.y + 1 && x0 + size.width / k >= b.x + b.width - 1 && y0 + size.height / k >= b.y + b.height - 1;
    })();
    const centerOn = (world: Point) => {
        stopAnimation();
        setView(v => ({ ...v, x: size.width / 2 - world.x * v.k, y: size.height / 2 - world.y * v.k }));
    };
    const hovered = hover ? seatById.get(hover.id) : undefined;
    const hoverContent = hovered ? tooltip(hovered) : null;
    const box = drag?.type === 'box' ? {
        x: Math.min(drag.a.x, drag.b.x), y: Math.min(drag.a.y, drag.b.y),
        width: Math.abs(drag.b.x - drag.a.x), height: Math.abs(drag.b.y - drag.a.y),
    } : null;

    return (
        <div ref={wrapRef} className="relative h-full w-full overflow-hidden bg-[#F4F5F7] dark:bg-[#E2E5E9]">
            <svg
                ref={svgRef}
                width={size.width}
                height={size.height}
                className={`block touch-none select-none ${drag?.type === 'pan' && drag.moved ? 'cursor-grabbing' : readOnly || pickMode ? 'cursor-grab' : 'cursor-crosshair'}`}
                onPointerDown={onPointerDown}
                onPointerMove={onPointerMove}
                onPointerUp={onPointerUp}
                onPointerCancel={e => {
                    pointers.current.delete(e.pointerId);
                    setDrag(null);
                }}
                onPointerLeave={() => setHover(null)}
                onContextMenu={e => e.preventDefault()}
            >
                <defs>
                    <pattern id={hatchId} width="5" height="5" patternUnits="userSpaceOnUse" patternTransform="rotate(45)">
                        <rect width="5" height="5" fill="#FDE68A" />
                        <line x1="0" y1="0" x2="0" y2="5" stroke="#D97706" strokeWidth="2" />
                    </pattern>
                </defs>
                <g transform={`translate(${view.x} ${view.y}) scale(${k})`}>
                    {layout.shapes.map(s => {
                        const { width: w, height: h } = s.size;
                        const isText = s.variant === 'text';
                        const fill = isText ? 'transparent' : s.color || (s.dashed ? '#BBF7D0' : '#FDE68A');
                        const stroke = isText ? 'transparent' : s.strokeColor || (s.dashed ? '#16A34A' : '#D97706');
                        const transform = `translate(${s.position.x} ${s.position.y}) rotate(${s.rotation} ${w / 2} ${h / 2})`;
                        if (s.variant === 'line' && s.points) {
                            return (
                                <g key={s.key} transform={transform} pointerEvents="none">
                                    <polyline points={s.points.map(p => `${p.x},${p.y}`).join(' ')} fill="none" stroke={s.strokeColor || '#374151'} strokeWidth={s.strokeWidth ?? 3} strokeLinecap="round" />
                                </g>
                            );
                        }
                        return (
                            <g key={s.key} transform={transform} pointerEvents="none">
                                {s.type === 'circle' && <ellipse cx={w / 2} cy={h / 2} rx={w / 2} ry={h / 2} fill={fill} stroke={stroke} strokeWidth={1.5} />}
                                {s.type === 'polygon' && s.points && <polygon points={s.points.map(p => `${p.x},${p.y}`).join(' ')} fill={fill} stroke={stroke} strokeWidth={1.5} />}
                                {s.type === 'square' && <rect width={w} height={h} rx={s.curveRadius ?? 0} fill={fill} stroke={stroke} strokeWidth={1.5} strokeDasharray={s.dashed ? '5,5' : undefined} />}
                                {isText
                                    ? <text x={w / 2} y={h / 2} dy=".35em" textAnchor="middle" fontSize={s.fontSize ?? 13} fontWeight={700} className="fill-gray-800 select-none">{s.name}</text>
                                    : <ShapeContent width={w} height={h} name={s.name} iconId={shapeIconId(s)} sub={s.variant === 'ga' ? 'General admission' : undefined} fontSize={13} color="#1F2937" />}
                            </g>
                        );
                    })}
                    {k >= 0.2 && layout.sections.map(sec => (
                        <text
                            key={sec.key}
                            x={sec.x}
                            y={sec.y}
                            dy=".35em"
                            textAnchor="middle"
                            fontSize={15}
                            fontWeight={700}
                            transform={`rotate(${sec.rotation} ${sec.x} ${sec.y})`}
                            className="fill-gray-700 select-none"
                            pointerEvents="none"
                        >
                            {sec.text}
                        </text>
                    ))}
                    {k >= 0.35 && layout.rows.map(r => r.label && r.side !== 'none' && (
                        <g key={r.id} className={readOnly || pickMode ? 'pointer-events-none' : 'cursor-pointer'}>
                            {r.side !== 'right' && (
                                <text data-row={r.id} x={r.left.x} y={r.left.y} dy=".35em" textAnchor="middle" fontSize={11} fontWeight={600} className="fill-gray-500 hover:fill-sky-500 select-none">{r.label}</text>
                            )}
                            {r.seatIds.length > 1 && r.side !== 'left' && (
                                <text data-row={r.id} x={r.right.x} y={r.right.y} dy=".35em" textAnchor="middle" fontSize={11} fontWeight={600} className="fill-gray-500 hover:fill-sky-500 select-none">{r.label}</text>
                            )}
                        </g>
                    ))}
                    <Seats seats={layout.seats} look={look} selection={selection} showNumbers={k >= 0.85} hatchId={hatchId} />
                    {box && <rect {...box} fill="#0EA5E9" fillOpacity={0.08} stroke="#0EA5E9" strokeWidth={1 / k} pointerEvents="none" />}
                </g>
            </svg>

            {hovered && hoverContent && !drag && (
                <div
                    className="pointer-events-none absolute z-10 min-w-[160px] max-w-[240px] rounded-md bg-gray-900/95 px-3 py-2 text-xs text-white shadow-lg"
                    style={{ left: Math.max(8, Math.min(hover!.x + 14, size.width - 250)), top: Math.max(8, hover!.y - 74) }}
                >
                    <div className="font-semibold">{seatTitle(hovered)}</div>
                    {hoverContent}
                    {hovered.accessible && <div className="mt-0.5 text-sky-300">Wheelchair accessible</div>}
                </div>
            )}

            {!coversAll && size.width > 0 && (
                <Minimap
                    layout={layout}
                    look={look}
                    selection={selection}
                    view={view}
                    size={size}
                    compact={size.width < 640}
                    onCenter={centerOn}
                />
            )}

            {pickMode && k < TAP_ZOOM_BELOW && !drag && (
                <div className="pointer-events-none absolute left-3 bottom-3 rounded-full bg-white/95 px-3 py-1.5 text-[11px] font-medium text-gray-700 shadow-sm dark:bg-nightElevated/95 dark:text-nightTextSecondary">
                    Tap or click the seats to zoom in
                </div>
            )}

            <div className="absolute right-3 bottom-3 flex items-center rounded-md border border-gray-200 bg-white shadow-sm dark:border-nightBorder dark:bg-nightElevated">
                <button type="button" aria-label="Zoom out" className="h-8 w-8 flex items-center justify-center text-gray-600 hover:bg-gray-100 dark:text-nightTextSecondary dark:hover:bg-nightSurface" onClick={() => zoomAt(0.8)}>
                    <Minus className="h-3.5 w-3.5" />
                </button>
                <span className="min-w-[44px] text-center text-xs tabular-nums text-gray-600 dark:text-nightTextSecondary">{Math.round(k * 100)}%</span>
                <button type="button" aria-label="Zoom in" className="h-8 w-8 flex items-center justify-center text-gray-600 hover:bg-gray-100 dark:text-nightTextSecondary dark:hover:bg-nightSurface" onClick={() => zoomAt(1.25)}>
                    <Plus className="h-3.5 w-3.5" />
                </button>
                <div className="h-5 w-px bg-gray-200 dark:bg-nightBorder" />
                <button type="button" aria-label="Fit to screen" className="h-8 w-8 flex items-center justify-center text-gray-600 hover:bg-gray-100 dark:text-nightTextSecondary dark:hover:bg-nightSurface" onClick={fit}>
                    <Maximize className="h-3.5 w-3.5" />
                </button>
            </div>
        </div>
    );
};

export default SeatingCanvas;
